import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "./Toast";
import { useI18n } from "../i18n";
import { useOnline } from "../lib/hooks";
import { flushOutbox, useOutbox } from "../lib/outbox";
import { jobKey, jobsKey, useApiReady, useApplyJob } from "../lib/jobsQueries";

const RETRY_MS = 30_000;

/**
 * ส่งคำสั่งที่ค้างในคิวออฟไลน์ (ไม่แสดงอะไรเอง)
 * ทำงานตอนเปิดแอป ตอน event online และเมื่อมีคำสั่งใหม่เข้าคิว และลองซ้ำทุก 30 วินาทีเมื่อยังมีคำสั่งค้างและออนไลน์
 * ถ้า server ตอบ 409 หรือคำสั่งใช้ไม่ได้ จะทิ้งคำสั่งที่เหลือของงานนั้น แจ้งผู้ใช้ และโหลดข้อมูลล่าสุดของงาน
 */
export function OutboxSync() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const apply = useApplyJob();
  const { ready } = useApiReady();
  const online = useOnline();
  const items = useOutbox();
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const handled = useRef<object | null>(null);
  const tRef = useRef(t);
  tRef.current = t;

  const flush = useCallback(async () => {
    if (!ready || (typeof navigator !== "undefined" && navigator.onLine === false)) return;
    const res = await flushOutbox({
      onSent: async (item, job) => {
        if (job) applyRef.current(job);
        else await qc.invalidateQueries({ queryKey: jobKey(item.jobId) });
      },
    });
    // การเรียกซ้อนได้ผลลัพธ์ก้อนเดียวกัน แจ้งผู้ใช้ครั้งเดียว
    if (handled.current === res) return;
    handled.current = res;
    if (res.dropped.length > 0) {
      toast({ message: tRef.current("web.q.conflict"), tone: "danger", duration: 8000 });
      for (const d of res.dropped) void qc.invalidateQueries({ queryKey: jobKey(d.jobId) });
      void qc.invalidateQueries({ queryKey: jobsKey });
    } else if (res.sent > 0) {
      toast({ message: tRef.current("web.q.sent") });
    }
  }, [ready, qc, toast]);

  const pending = items.length;
  useEffect(() => {
    if (online) void flush();
  }, [online, flush, pending]);

  // event online ส่งทันที (ถ้า navigator.onLine ยังเป็น true อยู่แล้ว state ของ useOnline จะไม่เปลี่ยน)
  useEffect(() => {
    const on = () => void flush();
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
  }, [flush]);

  useEffect(() => {
    if (pending === 0 || !online) return undefined;
    const timer = window.setInterval(() => void flush(), RETRY_MS);
    return () => window.clearInterval(timer);
  }, [pending, online, flush]);

  return null;
}
